import { Box, Typography } from "@mui/material";
import { FunctionComponent, useCallback, useEffect, useState } from "react";
import { FileUploader } from "react-drag-drop-files";
import { useTranslation } from "react-i18next";

import { faLoader } from "@fortawesome/pro-regular-svg-icons";

import UploadCloud from "@assets/icons/upload-cloud.svg?react";

import { useCourseFiles } from "@/pages/Private/helpers/useCourseFiles";

import { FileAssignment } from "@/pages/Private/pages/Courses/enums/FileAssignment.enum";

import { IFileInfo } from "@/pages/Private/pages/Courses/components/FilesUploader";

import { IStageContentInfo } from "@/pages/Private/pages/Courses/constants/emptyCourseScenario";

import { getAllErrors, renderErrorMessages } from "@/utils";

import { FileTypes } from "@/pages/Private/pages/Courses/enums/FileTypes.enum";

import { PickFromLibraryButton } from "@/pages/Private/pages/Courses/components/MediaLibrary/PickFromLibraryButton";

import { Icon } from "../Icon/Icon";
import { SvgIcon } from "../Icon/SvgIcon";
import VideoContainer from "./VideoContainer";

interface VideoUploaderProps {
	border?: boolean;
	label: string;
	description?: string;
	setCurrentVideoFileInfo: (fileInfo: IFileInfo | null) => void;
	currentVideoFileInfo?: IFileInfo | null;
	maxFileSizeInBytes?: number;
	videoContainerClassName?: string;
	videoPrefix?: string;
	fileAssignment: FileAssignment;
	setCurrentContentInfo?: (state: IStageContentInfo | null) => void;
	courseId?: number;
}

export const VideoUploader: FunctionComponent<VideoUploaderProps> = ({
	border,
	setCurrentVideoFileInfo,
	currentVideoFileInfo,
	label,
	description,
	videoContainerClassName = "max-h-[2vh] min-h-[10vh]",
	maxFileSizeInBytes = 500 * 1024 * 1024,
	videoPrefix = "uploaded-video",
	fileAssignment,
	setCurrentContentInfo,
	courseId,
}) => {
	const { t } = useTranslation();
	const { uploadCourseFiles, deleteCourseFile, isLoading, error } = useCourseFiles();
	const [uploadedFile, setUploadedFile] = useState<null | File>(null);

	const handleUpload = async (file: File) => {
		try {
			const fileExtension = file.name.split(".").pop();
			const newFileName = `${videoPrefix}.${fileExtension}`;

			const renamedFile = new File([file], newFileName, { type: file.type });

			const uploadedUrl = await uploadCourseFiles([renamedFile], fileAssignment, courseId);

			if (uploadedUrl?.length) {
				setCurrentVideoFileInfo(uploadedUrl[0] as IFileInfo);
			} else {
				throw new Error("Upload failed");
			}
		} catch (error) {
			console.error(error);
			setCurrentVideoFileInfo(null);
		}
	};

	const resetFile = useCallback(async () => {
		if (currentVideoFileInfo?.fullFilePath) {
			try {
				await deleteCourseFile(currentVideoFileInfo?.fullFilePath, courseId);
				setCurrentVideoFileInfo(null);
			} catch (error) {
				console.error("Error deleting video:", error);
			}
		}

		setUploadedFile(null);
	}, [currentVideoFileInfo, deleteCourseFile, setCurrentVideoFileInfo, courseId]);

	useEffect(() => {
		if (uploadedFile) {
			handleUpload(uploadedFile);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [uploadedFile]);

	return (
		<div className="w-full">
			{label && (
				<label className="ml-1 text-[14px] font-bold text-primary-gray mb-[6px]">{label}</label>
			)}
			{description && (
				<p className="ml-1 mt-1 text-[14px] text-primary-gray-lighter mb-[6px]">{description}</p>
			)}

			{/* The other way to fill this slot: something the company already has. Goes
			    through the library's assign path rather than an upload, so a file used here
			    and elsewhere is one file. */}
			<div className="mb-2 ml-1">
				<PickFromLibraryButton
					contentType={FileTypes.Video}
					courseId={courseId}
					fileAssignment={fileAssignment}
					onAssigned={(fileInfo) => setCurrentVideoFileInfo(fileInfo)}
				/>
			</div>
			<Box
				className=""
				display="flex"
				flexDirection="column"
				justifyContent="center"
				sx={{
					borderRadius: "12px",
					textAlign: "center",
					backgroundColor: "white",
					transition: "background-color 0.3s ease-in-out",
					...(border ? { border: "1px solid", borderColor: "#DCDAD6" } : { boxShadow: 1 }),
				}}
				textAlign="center"
				width="100%"
			>
				{!currentVideoFileInfo ? (
					<FileUploader
						disabled={isLoading}
						handleChange={(file: File | File[]) =>
							setUploadedFile(Array.isArray(file) ? file[0] : file)
						}
						maxSize={Math.round(maxFileSizeInBytes / 1024 / 1024)}
						name="file"
						types={["MP4", "MOV", "AVI"]}
					>
						<Box
							alignItems="center"
							color="gray600.main"
							display="flex"
							flexDirection="column"
							height="100%"
							justifyContent="center"
							sx={{ cursor: "pointer", minHeight: "100px" }}
							textAlign="center"
						>
							{isLoading ? (
								<Icon className="animate-spin" icon={faLoader} size="lg" />
							) : (
								<>
									<SvgIcon
										className="h-[40px] w-[40px] p-[10px] border rounded-lg border-gray-200 text-gray-600"
										svgIcon={UploadCloud}
									/>
									<Typography className="text-primary-gray" variant="body2">
										{t("courses.upload.title")}
									</Typography>

									<Typography className="text-primary-gray" variant="caption">
										{t("courses.upload.description", {
											sizeInMB: Math.round(maxFileSizeInBytes / 1024 / 1024),
										})}
									</Typography>
								</>
							)}
						</Box>
					</FileUploader>
				) : (
					<VideoContainer
						deleteFileHandler={resetFile}
						setCurrentContentInfo={setCurrentContentInfo}
						videoContainerClassName={videoContainerClassName}
						videoUrl={currentVideoFileInfo?.fullFilePath}
					/>
				)}
			</Box>
			{getAllErrors(error, []).length ? renderErrorMessages(getAllErrors(error, [])) : null}
		</div>
	);
};
